import { chatSnapshot } from '../../../../../particles/api/extension';
import type { DiagnosticsReport } from '../../../../../particles/api/extension';

export type FindingLevel = 'error' | 'warning' | 'info';

export interface Finding {
  level: FindingLevel;
  text: string;
}

/**
 * What a report says, in sentences support can act on.
 *
 * Every rule here reads only what the extension sent — the snapshot's counts,
 * the two parsers' summaries, the sync history and the error trail. The
 * snapshot carries no message text, so "the parser got this wrong" can't be
 * checked directly; what can be checked is where the extension disagrees with
 * itself (the DOM parser against the interceptor, rendered rows against parsed
 * messages) and where the server refused it.
 */
export function reportFindings(report: DiagnosticsReport): Finding[] {
  const out: Finding[] = [];
  const snap = chatSnapshot(report);

  const page = report.diagnostics?.page;
  if (report.diagnostics && 'page' in report.diagnostics && !page) {
    out.push({
      level: 'error',
      text:
        report.diagnostics.whatsAppTabs === 0
          ? 'No WhatsApp Web tab was open, so there is no page snapshot.'
          : 'A WhatsApp tab was open but the extension was not running in it — the tab needs reloading.'
    });
  }
  if (page?.state === 'qr') {
    out.push({ level: 'warning', text: 'WhatsApp was showing its QR code: the phone was not linked when this was sent.' });
  }
  const brokenSelectors = (page?.selectors ?? []).filter((s) => s.applicable && !s.ok);
  if (brokenSelectors.length > 0) {
    out.push({
      level: 'error',
      text: `The extension could not find ${brokenSelectors.map((s) => s.name).join(', ')} on the WhatsApp page — likely a WhatsApp markup change. See the page outline below.`
    });
  }

  // Reports from before the page snapshot (Oct 2026) carry neither.
  if (report.kind === 'manual' && !snap && page !== null) {
    const what = page ? 'there is a page snapshot but no chat snapshot' : 'there is no chat snapshot';
    out.push({
      level: 'warning',
      text:
        report.diagnostics?.trigger === 'cache-clear'
          ? `No WhatsApp chat was open when the cache was cleared, so ${what}.`
          : `No WhatsApp chat was open when this was sent, so ${what}. If the problem is in one chat, ask the customer to open it and send again.`
    });
  }

  const failedSyncs = (report.recentSyncAttempts ?? []).filter(
    (s) => s.serverStatus < 200 || s.serverStatus >= 300
  );
  if (failedSyncs.length > 0) {
    const last = failedSyncs[failedSyncs.length - 1];
    out.push({
      level: 'error',
      text: `${failedSyncs.length} recent sync${failedSyncs.length === 1 ? '' : 's'} failed on the server — last with ${last.serverStatus}${last.serverMessage ? `: ${last.serverMessage}` : ''}.`
    });
  }

  const errorCount = report.errors?.length ?? 0;
  if (errorCount > 0) {
    out.push({
      level: 'warning',
      text: `${errorCount} error${errorCount === 1 ? '' : 's'} recorded this session (listed below).`
    });
  }

  if (!snap) return out;

  if (snap.truncated) {
    out.push({
      level: 'info',
      text: 'The snapshot was trimmed to the most recent rows — older messages on screen are not included.'
    });
  }

  const rendered = snap.rowCount - snap.virtualizedCount;
  if (snap.virtualizedCount > 0) {
    out.push({
      level: 'info',
      text: `${snap.virtualizedCount} of ${snap.rowCount} message rows were off screen and not rendered by WhatsApp; the DOM parser cannot read those.`
    });
  }

  const dom = snap.dom;
  if (!dom) {
    out.push({ level: 'error', text: 'The DOM parser threw while building the snapshot.' });
  } else {
    if (rendered > 0 && dom.messages.length === 0) {
      out.push({
        level: 'error',
        text: `WhatsApp rendered ${rendered} rows but the DOM parser read no messages from them — the markup has likely changed. Download the rows below and re-run the parser on them.`
      });
    } else if (rendered > dom.messages.length) {
      out.push({
        level: 'info',
        text: `${rendered - dom.messages.length} rendered rows produced no message (media without a caption, system notices, or a parser miss).`
      });
    }
    if (dom.stats.parseErrorCount > 0) {
      out.push({
        level: 'error',
        text: `The DOM parser failed on ${dom.stats.parseErrorCount} row${dom.stats.parseErrorCount === 1 ? '' : 's'}.`
      });
    }
    if (dom.messages.length >= 3) {
      const sent = dom.messages.filter((m) => m.sent).length;
      if (sent === 0 || sent === dom.messages.length) {
        out.push({
          level: 'warning',
          text: `Every message the DOM parser read is ${sent === 0 ? 'incoming' : 'outgoing'}. That is possible, but it is also what a direction bug looks like.`
        });
      }
    }
  }

  const icpt = snap.interceptor;
  if (!icpt) {
    out.push({
      level: 'info',
      text: 'The interceptor had no messages for this chat, so syncs used the DOM parser.'
    });
  } else if (dom) {
    // Texts hash identically within one report, so the two parsers' views of
    // the same message can be lined up without either carrying the text.
    const icptByHash = new Map(icpt.messages.map((m) => [m.textHash, m]));
    const flipped = dom.messages.filter((m) => {
      const other = icptByHash.get(m.textHash);
      return other && other.sent !== m.sent;
    });
    if (flipped.length > 0) {
      out.push({
        level: 'error',
        text: `The two parsers disagree on direction for ${flipped.length} message${flipped.length === 1 ? '' : 's'} (marked below).`
      });
    }
    const domHashes = new Set(dom.messages.map((m) => m.textHash));
    const onlyInterceptor = icpt.messages.filter((m) => !domHashes.has(m.textHash)).length;
    const onlyDom = dom.messages.filter((m) => !icptByHash.has(m.textHash)).length;
    if (onlyDom > 0) {
      out.push({
        level: 'info',
        text: `${onlyDom} message${onlyDom === 1 ? '' : 's'} read from the page ${onlyDom === 1 ? 'has' : 'have'} no match in the interceptor's batch (different text extraction, or not loaded there).`
      });
    }
    if (onlyInterceptor > 0) {
      out.push({
        level: 'info',
        text: `The interceptor has ${onlyInterceptor} message${onlyInterceptor === 1 ? '' : 's'} not on screen — expected when older history is loaded.`
      });
    }
  }

  return out;
}
